import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useLangContext } from '../../providers/LangProvider/LangProviderContext.js';
import amountFormat from '../../utils/amountFormat.js';
import {
    NODE_HEIGHT,
    NODE_WIDTH,
    buildForest,
    collectAncestorIds,
    computeGraphLayout,
    defaultExpandedIds,
} from './partnerNetworkTreeUtils.js';
import {
    GraphCanvas,
    GraphControls,
    GraphHint,
    GraphNode,
    GraphSvg,
    GraphViewport,
    NodeChildrenBadge,
    NodeHeader,
    NodeLevel,
    NodeMeta,
    NodeName,
    NodeStatus,
} from './PartnerNetworkGraphStyled.js';

const MIN_SCALE = 0.35;
const MAX_SCALE = 1.6;

/**
 * @param {object} props
 * @param {object[]} props.flatNodes
 * @param {object|null} [props.branchRoot]
 * @param {number|null} [props.viewerUid]
 * @param {number|null} [props.selectedPartnerUid]
 * @param {number|null} [props.focusPartnerUid]
 * @param {number} [props.focusToken]
 * @param {boolean} [props.loading]
 * @param {(uid: number) => void} [props.onSelectPartner]
 * @param {(uid: number) => Promise<object[]>} [props.onExpandNode]
 */
function PartnerNetworkGraph({
    flatNodes,
    branchRoot = null,
    viewerUid = null,
    selectedPartnerUid = null,
    focusPartnerUid = null,
    focusToken = 0,
    loading = false,
    onSelectPartner,
    onExpandNode,
}) {
    const { getLangPath } = useLangContext();
    const viewportRef = useRef(null);
    const dragRef = useRef({ active: false, x: 0, y: 0, panX: 0, panY: 0 });
    const viewRef = useRef({ pan: { x: 24, y: 24 }, scale: 1 });
    const lastFocusTokenRef = useRef(0);

    const [expanded, setExpanded] = useState(() => defaultExpandedIds(flatNodes, branchRoot));
    const [scale, setScale] = useState(1);
    const [pan, setPan] = useState({ x: 24, y: 24 });
    const [loadingUid, setLoadingUid] = useState(null);

    useEffect(() => {
        viewRef.current = { pan, scale };
    }, [pan, scale]);

    const zoomAtPoint = useCallback((focusX, focusY, scaleDelta) => {
        const { pan: prevPan, scale: prevScale } = viewRef.current;
        const nextScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prevScale + scaleDelta));
        if (nextScale === prevScale) {
            return;
        }

        const worldX = (focusX - prevPan.x) / prevScale;
        const worldY = (focusY - prevPan.y) / prevScale;
        setScale(nextScale);
        setPan({
            x: focusX - worldX * nextScale,
            y: focusY - worldY * nextScale,
        });
    }, []);

    useEffect(() => {
        setExpanded((prev) => {
            const next = new Set(prev);
            for (const id of defaultExpandedIds(flatNodes, branchRoot)) {
                next.add(id);
            }
            return next;
        });
    }, [flatNodes, branchRoot]);

    const roots = useMemo(
        () => buildForest(flatNodes, branchRoot),
        [flatNodes, branchRoot],
    );

    const layout = useMemo(
        () => computeGraphLayout(roots, expanded),
        [roots, expanded],
    );

    useEffect(() => {
        if (!focusPartnerUid || !focusToken || focusToken === lastFocusTokenRef.current) {
            return;
        }

        const ancestorIds = collectAncestorIds(
            flatNodes,
            focusPartnerUid,
            branchRoot?.partner_uid ?? null,
        );
        if (ancestorIds.length) {
            setExpanded((prev) => {
                const next = new Set(prev);
                for (const id of ancestorIds) {
                    next.add(id);
                }
                return next;
            });
        }

        lastFocusTokenRef.current = focusToken;
        const target = layout.nodes.find((item) => item.node.partner_uid === focusPartnerUid);
        const viewport = viewportRef.current;
        if (!target || !viewport) {
            return;
        }

        const rect = viewport.getBoundingClientRect();
        setPan({
            x: rect.width / 2 - (target.x + NODE_WIDTH / 2) * scale,
            y: rect.height / 2 - (target.y + NODE_HEIGHT / 2) * scale,
        });
    }, [focusPartnerUid, focusToken, flatNodes, branchRoot, layout.nodes, scale]);

    const toggleExpand = useCallback(async (node) => {
        const uid = node.partner_uid;
        if (expanded.has(uid)) {
            setExpanded((prev) => {
                const next = new Set(prev);
                next.delete(uid);
                return next;
            });
            return;
        }

        const knownChildren = flatNodes.some(
            (item) => item.parent_partner_uid === uid
                || (node.depth === 0 && item.depth === 1 && !item.parent_partner_uid),
        );
        if (!knownChildren && onExpandNode && node.has_children) {
            setLoadingUid(uid);
            try {
                await onExpandNode(uid);
            } finally {
                setLoadingUid(null);
            }
        }

        setExpanded((prev) => new Set(prev).add(uid));
    }, [expanded, flatNodes, onExpandNode]);

    const onPointerDown = useCallback((e) => {
        if (e.button !== 0) {
            return;
        }
        dragRef.current = {
            active: true,
            x: e.clientX,
            y: e.clientY,
            panX: pan.x,
            panY: pan.y,
        };
    }, [pan.x, pan.y]);

    const onPointerMove = useCallback((e) => {
        if (!dragRef.current.active) {
            return;
        }
        setPan({
            x: dragRef.current.panX + (e.clientX - dragRef.current.x),
            y: dragRef.current.panY + (e.clientY - dragRef.current.y),
        });
    }, []);

    const onPointerUp = useCallback(() => {
        dragRef.current.active = false;
    }, []);

    const onWheel = useCallback((e) => {
        e.preventDefault();
        const viewport = viewportRef.current;
        if (!viewport) {
            return;
        }
        const rect = viewport.getBoundingClientRect();
        zoomAtPoint(e.clientX - rect.left, e.clientY - rect.top, -e.deltaY * 0.0015);
    }, [zoomAtPoint]);

    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport) {
            return undefined;
        }
        viewport.addEventListener('wheel', onWheel, { passive: false });
        return () => viewport.removeEventListener('wheel', onWheel);
    }, [onWheel]);

    if (!roots.length) {
        return <p className="text-muted">{getLangPath('network.graph_empty')}</p>;
    }

    return (
        <GraphViewport
            ref={viewportRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
        >
            <GraphControls>
                <Button size="sm" variant="secondary" onClick={() => setScale((s) => Math.min(MAX_SCALE, s + 0.1))}>+</Button>
                <Button size="sm" variant="secondary" onClick={() => setScale((s) => Math.max(MIN_SCALE, s - 0.1))}>−</Button>
            </GraphControls>

            <GraphHint>
                {getLangPath('network.graph_hint')}
                {loading || loadingUid ? ` · ${getLangPath('network.graph_loading')}` : ''}
            </GraphHint>

            <GraphCanvas style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})` }}>
                <GraphSvg width={layout.width} height={layout.height}>
                    {layout.edges.map((edge, index) => {
                        const midY = (edge.y1 + edge.y2) / 2;
                        const path = `M ${edge.x1} ${edge.y1} C ${edge.x1} ${midY}, ${edge.x2} ${midY}, ${edge.x2} ${edge.y2}`;
                        return (
                            <path
                                key={`edge-${index}`}
                                d={path}
                                fill="none"
                                stroke="rgba(68, 168, 128, 0.55)"
                                strokeWidth="2"
                            />
                        );
                    })}
                </GraphSvg>

                {layout.nodes.map(({ node, x, y }) => {
                    const level = node.depth ?? 0;
                    const isExpanded = expanded.has(node.partner_uid);
                    const isLoading = loadingUid === node.partner_uid;
                    const childrenCount = Number(node.children_count ?? node.children?.length ?? 0);
                    const hasChildren = Boolean(node.has_children || childrenCount > 0);
                    const isSelected = selectedPartnerUid === node.partner_uid
                        || (!selectedPartnerUid && viewerUid && node.partner_uid === viewerUid);
                    const isLimited = level >= 2 || (!node.full_name && !node.is_root && level > 0);
                    const displayName = isLimited
                        ? `#${node.partner_uid}`
                        : (node.full_name || `#${node.partner_uid}`);
                    const payout = node.expected_payout;

                    return (
                        <GraphNode
                            key={node.partner_uid}
                            data-graph-node
                            $width={NODE_WIDTH}
                            $height={NODE_HEIGHT}
                            $isRoot={Boolean(node.is_root || node.depth === 0)}
                            $selected={isSelected}
                            style={{ left: x, top: y }}
                            onClick={() => onSelectPartner?.(node.partner_uid)}
                        >
                            <NodeHeader>
                                <NodeLevel $isRoot={Boolean(node.is_root || node.depth === 0)}>
                                    L{level}
                                </NodeLevel>
                                <NodeStatus
                                    $active={node.active}
                                    title={node.active
                                        ? getLangPath('network.graph_active')
                                        : getLangPath('network.graph_inactive')}
                                />
                            </NodeHeader>
                            <NodeName title={displayName}>{displayName}</NodeName>
                            <NodeMeta>
                                {isLimited
                                    ? (payout != null ? `$${amountFormat(payout)}` : '')
                                    : `#${node.partner_uid}${payout != null ? ` · $${amountFormat(payout)}` : ''}`}
                            </NodeMeta>
                            {hasChildren && (
                                <NodeChildrenBadge
                                    type="button"
                                    $isExpanded={isExpanded}
                                    disabled={isLoading}
                                    title={isExpanded
                                        ? getLangPath('network.graph_collapse')
                                        : getLangPath('network.graph_expand', { count: childrenCount })}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpand(node);
                                    }}
                                >
                                    {isLoading ? '…' : childrenCount}
                                </NodeChildrenBadge>
                            )}
                        </GraphNode>
                    );
                })}
            </GraphCanvas>
        </GraphViewport>
    );
}

export default PartnerNetworkGraph;
